import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Share, Download } from 'lucide-react';
import { Button } from './ui/button';

const AddToHomeScreen = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Compute device type once using useMemo (not in effect)
  const { isIOS, isAndroid, isMobile } = useMemo(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(userAgent);
    const standalone = window.navigator.standalone;
    const android = /android/.test(userAgent);
    return {
      isIOS: ios && !standalone,
      isAndroid: android,
      isMobile: ios || android
    };
  }, []);

  useEffect(() => {
    // Check if already dismissed or not mobile
    const dismissed = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissed || !isMobile) {
      return;
    }

    // For Android - listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a short delay (user has just logged in)
      setTimeout(() => {
        setShowPrompt(true);
      }, 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS - show manual instructions after delay
    if (isIOS) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 2000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isMobile, isIOS]);

  const handleInstallClick = async () => {
    if (isAndroid && deferredPrompt) {
      // Android: Show native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        // User accepted the install prompt
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
      localStorage.setItem('pwa_prompt_dismissed', 'true');
    }
    // iOS users will follow manual instructions
  };

  const handleClose = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', 'true');
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />
      
      {/* Prompt Card */}
      <div className="relative bg-white rounded-t-3xl shadow-2xl p-6 max-w-md mx-auto border-t-4 border-teal-500">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="text-center">
          {/* Icon */}
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Download className="w-8 h-8 text-white" />
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Add MyVenueMate to Home Screen
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-600 mb-4">
            Install our app for quick and easy access anytime!
          </p>

          {/* iOS Instructions */}
          {isIOS && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm text-gray-700 font-medium mb-2">
                To install on iPhone/iPad:
              </p>
              <ol className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start">
                  <span className="font-bold mr-2">1.</span>
                  <span>
                    Tap the <Share className="w-4 h-4 inline mx-1" /> Share button at the bottom
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold mr-2">2.</span>
                  <span>
                    Select <Plus className="w-3 h-3 inline mx-1" /> &quot;Add to Home Screen&quot;
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold mr-2">3.</span>
                  <span>Tap &quot;Add&quot; in the top right corner</span>
                </li>
              </ol>
            </div>
          )}

          {/* Android Install Button */}
          {isAndroid && deferredPrompt && (
            <Button 
              onClick={handleInstallClick}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white font-semibold py-3 rounded-xl shadow-lg mb-3"
            >
              <Download className="w-5 h-5 mr-2" />
              Add to Home Screen
            </Button>
          )}

          {/* Maybe Later Button */}
          <button
            onClick={handleClose}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium"
          >
            Maybe Later
          </button>
        </div>
      </div>

      {/* Custom CSS for slide-up animation */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AddToHomeScreen;
